import { Component, Inject, OnInit } from '@angular/core';
import {
  MatDialogActions, MatDialogClose, MatDialogTitle,
  MatDialogContent, MatDialogRef, MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule } from '@angular/material/table';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormControl, FormGroup, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { CONSTANTS } from '../../../../Constants/Constants';
import { SolEstimateRiskData } from '../../../../models/sol-estimate-table/SolEstimateRiskData';
import { SolEstimateRiskDialogData } from '../../../../models/sol-estimate-table/SolEstimateRiskDialogData';
import { MultilingualCurrencyPipe } from '../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { debounceTime } from 'rxjs';
import { CommonDataService } from '../../../../services/common-data.service';
import { roundHalfEven9 } from '../../../../../types/number-extensions';
import { CommonMetaDataService } from '../../../../services/common-meta-data.service';
import { SolEstimateService } from '../../../../services/estimate/sol-estimate.service';
import { NumericMaskDirective } from '../../../../shared/numeric/numeric-mask.directive';
import { NumericFieldConfig } from '../../../../shared/numeric/numeric.types';
import { DecimalPrecisionPipe } from '../../../../pipes/decimal-precision-pipe/decimal-precision.pipe';
import { CloseOnEscapeDirective } from '../../../../directives/close-on-escape.directive';

@Component({
  selector: 'app-sol-estimate-risk-dialog',
  imports: [
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatDialogContent,
    TranslatePipe,
    MatButtonModule,
    MatTableModule,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MultilingualCurrencyPipe,
    NumericMaskDirective,
    DecimalPrecisionPipe,
    CloseOnEscapeDirective
  ],
  templateUrl: './sol-estimate-risk-dialog.component.html',
  styleUrl: './sol-estimate-risk-dialog.component.scss'
})
export class SolEstimateRiskDialogComponent implements OnInit {
  riskForm!: FormGroup<{
    rate: FormControl<number>;
  }>;
  existingRate: number = CONSTANTS.ZERO;
  displayedColumns: string[] = ['title', 'rate', 'dollars', 'tdcPercent'];
  minValue: number = CONSTANTS.FIVE;
  readonly maxValue: number = CONSTANTS.NINETY_NINE;

  /** Bounds mirror minValue/maxValue; built once those are resolved in ngOnInit. */
  rateConfig!: NumericFieldConfig;

  riskDataSource: SolEstimateRiskData[] = [
    {
      title: '',
      rate: 0,
      dollars: 0,
      tdcPercent: 0
    }
  ];

  constructor(
    private readonly fb: NonNullableFormBuilder,
    public dialogRef: MatDialogRef<SolEstimateRiskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SolEstimateRiskDialogData,
    private readonly translate: TranslateService,
    private readonly commonDataservice: CommonDataService,
    private readonly commonMetaDataService: CommonMetaDataService,
    private readonly solEstimateService: SolEstimateService
  ) {
  }

  ngOnInit(): void {
    this.initializeForm();
    this.processDialogData();
    this.rateConfig = {
      kind: 'percentage',
      allowNegative: false,
      min: this.minValue,
      max: this.maxValue,
      step: CONSTANTS.ZERO_POINT_ONE,
      useGrouping: false,
      revertToLastValidValue: true,
    };
    this.riskForm.controls.rate.valueChanges
      .pipe(
        debounceTime(CONSTANTS.THREE_HUNDRED)
      ).subscribe(value => {
        if (value !== null && value !== undefined) {
          this.calculateCost();
        }
      });
  }

  private initializeForm(): void {
    const estimateMetaData = this.commonDataservice.getEstimateMetaData();
    this.existingRate = estimateMetaData?.estimateSettings?.autoRiskPercentage ?? CONSTANTS.ZERO;
    this.riskForm = this.fb.group({
      rate: this.fb.control(this.existingRate)
    });
  }

  private processDialogData(): void {
    if (this.data && this.data.subtotal) {
      const tdcPercent = this.calculateTdcPercent(this.data.subtotal, this.data.riskCost);
      this.riskDataSource[CONSTANTS.ZERO].title = this.translate.instant('SOL_ESTIMATE_RISK');
      this.riskDataSource[CONSTANTS.ZERO].dollars = this.data.riskCost;
      this.riskDataSource[CONSTANTS.ZERO].tdcPercent = tdcPercent;
      this.minValue = this.commonMetaDataService.getMetaData()?.minAutoRiskPercent ?? CONSTANTS.FIVE;
    }
  }

  private calculateTdcPercent(total: number, cost: number): number {
    if (total === CONSTANTS.ZERO || cost === CONSTANTS.ZERO) {
      return CONSTANTS.ZERO;
    }

    return roundHalfEven9((cost / total) * CONSTANTS.HUNDRED);
  }


  onSave(): void {
    const currentRate = this.riskForm.controls.rate.value;
    const clamped = Math.min(Math.max(currentRate, this.minValue), this.maxValue);
    if (clamped !== currentRate) {
      this.riskForm.controls.rate.setValue(clamped);
      this.calculateCost();
    }
    const result = {
      riskData: this.riskDataSource[CONSTANTS.ZERO],
    };
    if (this.riskForm.controls.rate.value !== this.existingRate) {
      this.commonDataservice.isSaveEnabled = true;
      const estimateMetaData = this.commonDataservice.getEstimateMetaData();
      if (estimateMetaData?.estimateSettings) {
        estimateMetaData.estimateSettings.autoRiskPercentage = this.riskForm.controls.rate.value;
        this.commonDataservice.patchEstimateMetaData(estimateMetaData);
      }
    }
    this.dialogRef.close(result);
  }


  calculateCost() {
    const rate = this.riskForm.get('rate').value;
    if (this.data && rate !== null && rate !== undefined) {
      const totalCost = this.solEstimateService.estimateModel.miscRiskTravelCosts.tdcForRisk ?? CONSTANTS.ZERO;
      const calculatedCost = roundHalfEven9((totalCost * rate) / CONSTANTS.HUNDRED);
      this.riskDataSource[CONSTANTS.ZERO].dollars = calculatedCost;
      this.riskDataSource[CONSTANTS.ZERO].tdcPercent = this.calculateTdcPercent(this.data.subtotal, calculatedCost);
    }
  }

}